import { Grid, useMediaQuery } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Card, CardContent, CardMedia } from 'src/components/Card';
import { Chip } from 'src/components/Chip';
import { Dialog } from 'src/components/Dialog';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { Cart } from 'src/models/cart/_types';
import { Product, ProductVariant } from 'src/models/product/_types';
import {
  getMaxAllowedQuantity,
  getStockAvailabilityMessage,
  isProductOutOfStock,
  isVariantOutOfStock,
} from 'src/models/product/utils';
import { CartDialog } from 'src/shared/CartDialog';
import { Identifier } from 'src/types/utilityTypes';
import { formatCurrency } from 'src/utils/numberFormatter';
import toast from 'src/utils/toast';

const ProductImage = styled('img')(({ theme }) => ({
  width: '100%',
  aspectRatio: '1 / 1',
  objectFit: 'cover',
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.vars.palette.divider}`,
}));

const PriceDisplay = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '1.5rem',
  color: theme.vars.palette.primary.main,
}));

const VariantGrid = styled(Box)(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12,
}));

const getVariantCardStyles = (theme: any, selected: boolean) => ({
  minWidth: 120,
  maxWidth: 120,
  cursor: 'pointer',
  border: selected
    ? `2px solid ${theme.vars.palette.primary.main}`
    : `1px solid ${theme.vars.palette.divider}`,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
});

const VariantImage = styled(CardMedia)(() => ({
  height: 80,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
}));

const VariantPrice = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.vars.palette.primary.main,
}));

interface PurchaseProductDialogProps {
  open: boolean;
  onClose: () => void;
  product: Product;
  preSelectedVariantId?: string;
  locationId?: string | Identifier;
  onAddToBag?: (
    cart: Cart | null,
    product: Product,
    selectedVariant?: ProductVariant
  ) => void;
  onBuyNow?: (
    cart: Cart | null,
    product: Product,
    selectedVariant?: ProductVariant
  ) => void;
  cart: Cart | null;
  addToCart: (item: any, options?: any) => Promise<void>;
  publishingChannel?: string;
  canManageAll?: boolean;
}

export const PurchaseProductDialog: FC<PurchaseProductDialogProps> = ({
  open,
  onClose,
  product,
  preSelectedVariantId,
  locationId,
  onAddToBag,
  onBuyNow,
  cart,
  addToCart,
  publishingChannel = 'checkout',
  canManageAll,
}) => {
  const { session } = useOptionalSession();
  const { t } = useTranslation('translation', {
    keyPrefix: 'purchase.productDialog',
  });
  const theme = useTheme();
  const isMediumDown = useMediaQuery(theme.breakpoints.down('lg'));
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(
    preSelectedVariantId || null
  );
  const [cartDialogOpen, setCartDialogOpen] = useState(false);
  const [selectedOptionValues, setSelectedOptionValues] = useState<
    Record<string, string>
  >({});

  const handleCloseDialog = useCallback(() => {
    setCartDialogOpen(false);
  }, []);

  const showLargerGrid =
    (product?.productVariants?.length || 0) > 1 ||
    publishingChannel === 'staff';

  // Staff with canManageAll permission can select out-of-stock items
  const canSelectOutOfStock = publishingChannel === 'staff' && canManageAll;

  // Filter variants to only show those available for checkout
  const checkoutVariants = useMemo(
    () =>
      product?.productVariants?.filter((variant) =>
        variant.publishingChannels?.includes(publishingChannel)
      ) || [],
    [product?.productVariants, publishingChannel]
  );

  // Filter variants by selected option values
  const filteredVariants = useMemo(() => {
    if (!Object.keys(selectedOptionValues).length) {
      return checkoutVariants;
    }

    return checkoutVariants.filter((variant) => {
      return Object.entries(selectedOptionValues).every(
        ([optionName, optionValue]) => {
          if (!optionValue) return true; // If no filter value selected, include all
          return variant.optionValues?.[optionName] === optionValue;
        }
      );
    });
  }, [checkoutVariants, selectedOptionValues]);

  // Separate available and out-of-stock variants from filtered variants
  const availableVariants = useMemo(
    () =>
      filteredVariants.filter(
        (variant) => !isVariantOutOfStock(variant, locationId, cart)
      ),
    [filteredVariants, locationId, cart]
  );

  const outOfStockVariants = useMemo(
    () =>
      filteredVariants.filter((variant) =>
        isVariantOutOfStock(variant, locationId, cart)
      ),
    [filteredVariants, locationId, cart]
  );

  // Variants that can be selected (includes out-of-stock for staff with permissions)
  const selectableVariants = useMemo(
    () =>
      canSelectOutOfStock
        ? filteredVariants // Include all variants (available + out-of-stock)
        : availableVariants, // Only available variants for regular users
    [canSelectOutOfStock, filteredVariants, availableVariants]
  );

  // Reset option values when product changes
  useEffect(() => {
    setSelectedOptionValues({});
  }, [product]);

  // Auto-select variant when product changes or variant is pre-selected
  useEffect(() => {
    if (!product || !selectableVariants.length) {
      setSelectedVariantId(null);
      return;
    }

    // First try to find pre-selected variant among selectable variants
    let targetVariantId = selectableVariants[0].id.toString();
    if (preSelectedVariantId) {
      const preSelectedVariant = selectableVariants.find(
        (variant) => variant.id.toString() === preSelectedVariantId
      );
      if (preSelectedVariant) {
        targetVariantId = preSelectedVariant.id.toString();
      }
    }

    setSelectedVariantId(targetVariantId);
  }, [product, preSelectedVariantId, selectableVariants]);

  // Find the selected variant based on selectedVariantId from selectable variants
  const selectedVariant = selectableVariants.find(
    (variant) => variant.id.toString() === selectedVariantId
  );

  // Use variant price if available, otherwise use product price
  const displayPrice = selectedVariant?.price ?? product?.price ?? 0;

  // Use variant image if available, otherwise use product image
  const displayImage = selectedVariant?.imageUrl ?? product?.imageUrl ?? '';

  const handleVariantSelect = useCallback((variantId: string) => {
    setSelectedVariantId(variantId);
  }, []);

  const handleOptionValueChange = useCallback(
    (optionName: string, optionValue: string) => {
      setSelectedOptionValues((prev) => {
        const currentValue = prev[optionName];
        // Toggle selection: if same value is clicked, clear it; otherwise set new value
        return {
          ...prev,
          [optionName]: currentValue === optionValue ? '' : optionValue,
        };
      });
    },
    []
  );

  const handleAddToBag = useCallback(async () => {
    if (product && selectedVariant) {
      // Check if the variant has adequate stock for the requested quantity
      const maxAllowed = getMaxAllowedQuantity(
        selectedVariant,
        locationId,
        cart
      );

      if (maxAllowed < 1 && !canManageAll) {
        // Show stock availability message
        const stockMessage = getStockAvailabilityMessage(
          selectedVariant,
          locationId,
          cart
        );
        const message = stockMessage || 'This item is out of stock';
        // Use toast from the existing import structure
        toast.error(message);
        return;
      }

      try {
        await addToCart(selectedVariant, {
          type: 'product-variant',
          quantity: 1,
          metadata: {
            productId: product.id,
            productName: product.name,
            variantDisplayName: selectedVariant.displayName,
            variantSku: selectedVariant.sku,
            variantBarcode: selectedVariant.barcode,
          },
        });
        onAddToBag?.(cart, product, selectedVariant);

        // In simplified mode, close the dialog after adding to bag
        if (publishingChannel === 'staff') {
          onClose();
        }
      } catch (error) {
        // Toast error is already handled in the cart context
      }
    }
  }, [
    product,
    selectedVariant,
    locationId,
    cart,
    canManageAll,
    addToCart,
    onAddToBag,
    publishingChannel,
    onClose,
  ]);

  const handleBuyNow = useCallback(async () => {
    if (product && selectedVariant) {
      onBuyNow?.(cart, product, selectedVariant);
    }
  }, [product, selectedVariant, onBuyNow, cart]);

  // Check if a variant is selected and available for checkout
  const hasRequiredSelections =
    !!selectedVariantId && !!selectedVariant && selectableVariants.length > 0;

  const actions =
    publishingChannel === 'staff'
      ? [
          {
            label: t('buttons.addToBag'),
            onClick: handleAddToBag,
            variant: 'contained' as const,
            disabled: !hasRequiredSelections,
          },
        ]
      : [
          {
            label: t('buttons.addToBag'),
            onClick: handleAddToBag,
            variant: 'outlined' as const,
            disabled: !hasRequiredSelections,
          },
          {
            label: t('buttons.buyNow', {
              price: formatCurrency(displayPrice, {
                cents: true,
                currency: session?.defaultCurrency || 'usd',
              }),
            }),
            onClick: handleBuyNow,
            variant: 'contained' as const,
            disabled: !hasRequiredSelections,
          },
        ];

  if (!product) {
    return null;
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        title={product.name}
        actions={actions}
        maxWidth={showLargerGrid ? 'md' : 'sm'}
        fullScreen={isMediumDown}
      >
        <Grid container spacing={3}>
          {/* Product Image */}
          <Grid size={{ md: showLargerGrid ? 6 : 12, sm: 12 }}>
            <ProductImage
              src={displayImage}
              alt={product.name}
              onError={(e) => {
                // Fallback to a placeholder if image fails to load
                e.currentTarget.src = '/images/product-placeholder.png';
              }}
            />

            {/* Variant Grid */}
            <PriceDisplay>
              {formatCurrency(displayPrice, {
                cents: true,
                currency: session?.defaultCurrency || 'usd',
              })}
            </PriceDisplay>

            {isProductOutOfStock(product, 'checkout', locationId, cart) && (
              <Typography
                variant="body2"
                color="error.main"
                fontWeight="600"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                {t('outOfStock')}
              </Typography>
            )}

            {/* Selected Variant Info */}
            {!!(
              (product?.productVariants?.length || 0) > 1 &&
              selectedVariant &&
              selectedVariant.displayName
            ) && (
              <Box
                sx={{
                  pt: 1,
                  pb: 3,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2" fontWeight={500}>
                  {t('selected')}: {selectedVariant.displayName}
                </Typography>

                {/* Stock availability message */}
                {selectedVariant &&
                  (() => {
                    const stockMessage = getStockAvailabilityMessage(
                      selectedVariant,
                      locationId,
                      cart
                    );
                    return stockMessage ? (
                      <Typography
                        variant="caption"
                        color="warning.main"
                        sx={{ display: 'block', mt: 0.5 }}
                      >
                        {stockMessage}
                      </Typography>
                    ) : null;
                  })()}
              </Box>
            )}

            {/* Description */}
            {product.description && (
              <Box>
                <Typography variant="h6">{t('aboutThisItem')}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {product.description}
                </Typography>
              </Box>
            )}
          </Grid>

          {/* Product Details */}
          <Grid size={{ md: showLargerGrid ? 6 : 12, sm: 12 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Price */}

              {/* Product Option Filters */}
              {product?.productOptions && product.productOptions.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Stack spacing={1.5}>
                    {product.productOptions.map((option) => (
                      <Box key={option.name}>
                        <Typography
                          variant="subtitle2"
                          sx={{ mb: 0.5, fontWeight: 600 }}
                        >
                          {option.name}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          flexWrap="wrap"
                          useFlexGap
                        >
                          {option.values.map((value) => (
                            <Chip
                              key={value}
                              label={value}
                              clickable
                              size="small"
                              variant={
                                selectedOptionValues[option.name] === value
                                  ? 'soft'
                                  : 'outlined'
                              }
                              color={
                                selectedOptionValues[option.name] === value
                                  ? 'primary'
                                  : 'secondary'
                              }
                              onClick={() =>
                                handleOptionValueChange(option.name, value)
                              }
                            />
                          ))}
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {showLargerGrid && (
                <Box>
                  <VariantGrid>
                    {/* Available variants */}
                    {availableVariants.map((variant) => (
                      <Card
                        key={variant.id}
                        sx={getVariantCardStyles(
                          theme,
                          variant.id.toString() === selectedVariantId
                        )}
                        onClick={() =>
                          handleVariantSelect(variant.id.toString())
                        }
                      >
                        <VariantImage
                          image={variant.imageUrl || product.imageUrl}
                          title={variant.displayName || 'Variant'}
                        />
                        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                          <Typography
                            variant="caption"
                            component="div"
                            sx={{
                              fontWeight: 500,
                              lineHeight: 1.2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {variant.displayName ||
                              Object.values(variant.optionValues || {}).join(
                                ', '
                              ) ||
                              'Standard'}
                          </Typography>
                          <VariantPrice variant="caption">
                            {formatCurrency(variant.price, {
                              cents: true,
                              currency: session?.defaultCurrency || 'usd',
                            })}
                          </VariantPrice>
                        </CardContent>
                      </Card>
                    ))}

                    {/* Out of stock variants */}
                    {outOfStockVariants.map((variant) => (
                      <Card
                        key={variant.id}
                        sx={{
                          ...getVariantCardStyles(
                            theme,
                            variant.id.toString() === selectedVariantId
                          ),
                          opacity: canSelectOutOfStock ? 1 : 0.5,
                          cursor: canSelectOutOfStock
                            ? 'pointer'
                            : 'not-allowed',
                          position: 'relative',
                          '&:hover': canSelectOutOfStock
                            ? {
                                transform: 'translateY(-2px)',
                                boxShadow: theme.shadows[4],
                              }
                            : {
                                transform: 'none',
                                boxShadow: 'none',
                              },
                        }}
                        onClick={
                          canSelectOutOfStock
                            ? () => handleVariantSelect(variant.id.toString())
                            : undefined
                        }
                      >
                        <Box sx={{ position: 'relative' }}>
                          <VariantImage
                            image={variant.imageUrl || product.imageUrl}
                            title={variant.displayName || 'Variant'}
                          />
                          {/* Out of stock overlay */}
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              backgroundColor: 'rgba(0, 0, 0, 0.6)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="white"
                              fontWeight="600"
                              sx={{
                                textAlign: 'center',
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                              }}
                            >
                              {t('outOfStock')}
                            </Typography>
                          </Box>
                        </Box>
                        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                          <Typography
                            variant="caption"
                            component="div"
                            sx={{
                              fontWeight: 500,
                              lineHeight: 1.2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              color: canSelectOutOfStock
                                ? 'text.primary'
                                : 'text.disabled',
                            }}
                          >
                            {variant.displayName ||
                              Object.values(variant.optionValues || {}).join(
                                ', '
                              ) ||
                              'Standard'}
                          </Typography>
                          <VariantPrice
                            variant="caption"
                            sx={{
                              color: canSelectOutOfStock
                                ? 'primary.main'
                                : 'text.disabled',
                            }}
                          >
                            {formatCurrency(variant.price, {
                              cents: true,
                              currency: session?.defaultCurrency || 'usd',
                            })}
                          </VariantPrice>
                        </CardContent>
                      </Card>
                    ))}
                  </VariantGrid>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Dialog>

      {publishingChannel === 'checkout' && (
        <CartDialog open={cartDialogOpen} onClose={handleCloseDialog} />
      )}
    </>
  );
};
